import { useState } from 'react';
import useSessionStorage from './useSessionStorage';
import { useHistory } from 'react-router-dom';
import { EnrollmentStatus } from 'Utils/Constants';
import { APP_ROOT } from 'Utils/EnvVars';
import { faro } from '@grafana/faro-react';

/**
 * Get and set the Patron's session data.
 */
function useSession() {
  /** @type {[Session, React.Dispatch<Session>]} */
  const [session, setSession] = useSessionStorage('vip-patron-session');
  const history = useHistory();
  const [logSession, setLogSession] = useState(null);

  if ((history?.location.pathname !== "/expired" && session?.isExpired) ||
  (session?.expires && Date.parse(session.expires) <= Date.now())) {
    if(!history?.location.pathname.includes('expired')) {
      history?.push("/expired");
    }
  }

  /**
   * Fetch session information from the server and store it.
   * @param {string} sessionId
   */
  const fetchSession = async (transactionSucceeded, isOAuthRedirect = false, sessionId = session.sessionId) => {
    if (!sessionId) return;
    const headers = {
      'session-id': sessionId
    };
    const response = await fetch(`${APP_ROOT}/api/patronsession/${transactionSucceeded}/${isOAuthRedirect}`, {
      method: "GET",
      headers,
    });
    switch (response.status) {
      case 200:
        /** @type {Session} Patron session data from the server.*/
        const data = await response.json();
        const isVipEnrolled = data.enrollmentStatusCode !== EnrollmentStatus.NOT_ENROLLED;
        setSession({ ...data, isVipEnrolled, isExpired: false });
        const enrollmentStatus = data.enrollmentStatusCode === 0 ? "new" : "existing";
        setLogSession({
          id: sessionId ? sessionId : data.sessionId,
          attributes: {
            patronId: data.patronId ? data.patronId : null,
            operatorName: data.operatorName ? data.operatorName : null,
            vipCardNumber: data.vipCardNumber ? data.vipCardNumber : null,
            transactionId: data.transactionId ? data.transactionId : null,
            transactionAmount: data.transactionAmount ? data.transactionAmount.toString() : null
          }
        });
        // set GTM values
        window.dataLayer.push({
          "event": "getSession",
          "operatorName": data.operatorName,
          "cultureCode": data.cultureCode,
          "enrollmentStatus": enrollmentStatus,
          "productType": data.productType
        });


        break;
      case 404:
        setSession({ isExpired: true });
        break;
      default:
        setSession(null);
        throw response;
    };
  };

  const invalidateSession = () => {
    if (!session && !session.sessionId) return;
    const headers = {
      "session-id": session.sessionId
    };
    (async () => {
      const response = await Promise.all([fetch(`${APP_ROOT}/api/patronsession/clearsession`, {
        method: "DELETE",
        headers,
      })]);
      if (response) {
        if (response.ok) {
          faro.pause();
        }
        setSession(undefined);
        localStorage.removeItem("vip-patron-session");
        sessionStorage.clear();
        localStorage.clear();
      }
    })();
  };

  const updateSession = (expiryTime) => {
    if (!session && !session.sessionId) return;
    session.expires = expiryTime;
  }

  // Shorthands
  const sessionId = session?.sessionId;

  return { sessionId, logSession, session, fetchSession, invalidateSession, updateSession };
};

export default useSession;
